import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import ContactForm from '../components/ContactForm';
import Lightbox from '../components/Lightbox';
import SEO from '../components/SEO';
import { getIconClass, sortServices } from '../lib/utils';

const TESTIMONIALS = [
  {
    quote: 'Det var nemt da holdet fra pabstteknik valgte at tage projektet. De var alle venlige og de fik mig til at føle mig tryg. Det endelige resultat endte med at blive bedre, end jeg kunne have forestillet mig!',
    author: 'Eva Sørensen',
    role: 'Produkt Designer'
  },
  {
    quote: 'Det klart bedste hold, jeg nogensinde har arbejdet med. De forstod virkelig opgaven og klarede det på rekord tid! Jeg vil varmt anbefale dem som virksomhed, du finder simpelthen ikke noget bedre team!',
    author: 'Bjarne Hansen',
    role: 'Afdelingsleder'
  },
  {
    quote: 'Pabst teknik er langt det bedste port firma derude! Jeg kunne bogstaveligt talt ikke være mere glad for, at jeg valgte at købe porte hos dem og fremragende service!',
    author: 'Lars Hansen',
    role: 'Produktionsleder'
  },
  {
    quote: 'Jeg var rystet da vi fik en skade på vores nye garage port, men da jeg ringede var de rolige og venlige og sendte en ud med det samme. Tak for en fantastisk venlig hjælp og hurtig udrykning!',
    author: 'Rikke Andersen',
    role: 'Salgselev'
  }
];

export default function Home() {
  const [services, setServices] = useState([]);
  const [galleryImages, setGalleryImages] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [isVideoOpen, setIsVideoOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [visibleSlides, setVisibleSlides] = useState(3);
  const [currentTestimonialSlide, setCurrentTestimonialSlide] = useState(0);
  const [isTestimonialPaused, setIsTestimonialPaused] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setVisibleSlides(3);
      } else if (window.innerWidth >= 768) {
        setVisibleSlides(2);
      } else {
        setVisibleSlides(1);
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const nextSlide = () => {
    if (currentIndex < galleryImages.length - visibleSlides) {
      setCurrentIndex(prev => prev + 1);
    }
  };

  const prevSlide = () => {
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
    }
  };

  useEffect(() => {
    // Fetch services from CMS
    fetch('https://api.dev.auxo.dk/public/content/services')
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data)) {
          setServices(data);
        }
      })
      .catch(err => console.error('Error fetching services:', err));

    // Fetch gallery images from CMS (combine all images for a rich slider)
    fetch('https://api.dev.auxo.dk/public/content/gallery')
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data) && data.length > 0) {
          const allImages = data.reduce((acc, current) => {
            if (Array.isArray(current.images)) {
              return [...acc, ...current.images];
            }
            return acc;
          }, []);
          const uniqueImages = Array.from(new Set(allImages));
          setGalleryImages(uniqueImages);
        }
      })
      .catch(err => console.error('Error fetching gallery:', err));
  }, []);

  return (
    <div className="overflow-hidden bg-brand-bg">
      <SEO
        title="Portservice, Lovpligtigt Eftersyn & Porte i Vejen"
        description="Professionel portservice, lovpligtigt eftersyn og montage af garageporte, hurtigporte og industriporte i Vejen og hele Syddanmark. Autoriseret forhandler af Ajax alarmsystemer."
        url="/"
        keywords="portservice, portservice Vejen, lovpligtigt eftersyn porte, garageporte, industriporte, hurtigporte, Ajax alarmsystem, videoovervågning, Pabst Teknik"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'HomeAndConstructionBusiness',
          name: 'Pabst Teknik ApS',
          image: 'https://assets.auxodev.dk/cdn-cgi/image/width=1200,quality=85,format=auto/assets/a2859b51-1b34-4d15-a97b-9fa5c0c0144f/webp-portservise-cover-2-85-11zon.webp',
          telephone: '+4521684422',
          email: 'kontakt@pabstteknik.dk',
          address: {
            '@type': 'PostalAddress',
            streetAddress: 'Gestenvej 29',
            addressLocality: 'Vejen',
            postalCode: '6600',
            addressCountry: 'DK'
          },
          geo: {
            '@type': 'GeoCoordinates',
            latitude: 55.4854,
            longitude: 9.1415
          },
          url: 'https://pabstteknik.dk',
          openingHoursSpecification: [
            {
              '@type': 'OpeningHoursSpecification',
              dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
              opens: '07:00',
              closes: '16:00'
            }
          ],
          priceRange: '$$',
          description: 'Professionel portservice, montering og reparation af garageporte, industriporte og intelligente Ajax alarmsystemer i Vejen og hele Danmark.'
        }}
      />

      {/* Hero Section */}
      <section className="relative px-6 pt-12 pb-24 md:py-32 lg:py-40 overflow-hidden bg-slate-950">
        {/* Background Video */}
        <div className="absolute inset-0 z-0 overflow-hidden">
          <video
            autoPlay
            loop
            muted
            playsInline
            aria-label="Pabst Teknik introduktionsvideo af portmontage og servicearbejde"
            className="h-full w-full object-cover"
          >
            <source
              src="https://assets.auxodev.dk/assets/a2859b51-1b34-4d15-a97b-9fa5c0c0144f/pabstteknikprivat-final16-9-1-1-1-1.mp4"
              type="video/mp4"
            />
            Din browser understøtter ikke afspilning af MP4 video.
          </video>
          {/* Overlay to ensure text readability */}
          <div className="absolute inset-0 bg-slate-950/70" />
        </div>

        <div className="relative z-10 mx-auto max-w-7xl">
          <div className="grid grid-cols-1 items-center gap-16 lg:grid-cols-12">
            {/* Hero Text */}
            <div className="lg:col-span-7 flex flex-col gap-8">
              <div className="inline-flex items-center gap-2 rounded-full bg-brand-accent/20 border border-brand-accent/30 px-3 py-1.5 text-[10px] sm:text-xs font-bold text-brand-accent-light uppercase tracking-wider self-start sm:px-4 sm:py-2 sm:gap-2.5">
                <span className="h-2 w-2 rounded-full bg-brand-accent animate-pulse" />
                Lokal Ekspertise & Rådgivning i Vejen
              </div>
              <h1 className="text-3xl font-extrabold tracking-tight text-white sm:text-5xl md:text-6xl leading-[1.1]">
                Portservice i<br />
                Syd-østjylland til<br />
                <span className="text-brand-accent">private og erhverv</span>
              </h1>
              <p className="text-lg text-slate-200 leading-relaxed max-w-2xl">
                Hos Pabst Teknik hjælper vi private og erhverv med portservice, montage og reparation af porte. Fra vores base i Vejen arbejder vi med garageporte, industriporte og automatik, så du får en port, der passer til bygningen, brugen og dine krav til sikkerhed. Vi servicerer desuden forskellige fabrikater.
              </p>

              {/* Hero CTAs */}
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
                <a
                  href="tel:+4521684422"
                  className="flex items-center justify-center gap-2.5 rounded-full bg-white px-5 py-3 text-sm sm:px-8 sm:py-4 sm:text-base font-bold text-brand-dark shadow-lg transition-all hover:bg-slate-100 hover:scale-[1.02]"
                >
                  <i className="fa-regular fa-phone text-brand-accent text-base sm:text-lg" />
                  <span>Ring op: 21 68 44 22</span>
                </a>
                <Link
                  to="/kontakt"
                  className="flex items-center justify-center gap-2 rounded-full bg-brand-accent px-5 py-3 text-sm sm:px-8 sm:py-4 sm:text-base font-bold text-white shadow-lg transition-all hover:bg-brand-accent-hover hover:scale-[1.02]"
                >
                  <span>Få et gratis tilbud</span>
                  <i className="fa-regular fa-arrow-right" />
                </Link>
              </div>

              {/* Trust Badges */}
              <div className="grid grid-cols-3 gap-4 border-t border-white/10 pt-8 mt-4">
                <div className="flex flex-col gap-1 transition-all duration-300 hover:-translate-y-1 group cursor-default">
                  <span className="text-xl font-extrabold text-white font-display group-hover:text-brand-accent transition-colors duration-300">100%</span>
                  <span className="text-xs font-semibold text-slate-300 uppercase tracking-wider">Dansk Kvalitet</span>
                </div>
                <div className="flex flex-col gap-1 transition-all duration-300 hover:-translate-y-1 group cursor-default">
                  <span className="text-xl font-extrabold text-white font-display group-hover:text-brand-accent transition-colors duration-300">24/7</span>
                  <span className="text-xs font-semibold text-slate-300 uppercase tracking-wider">Akut Portservice</span>
                </div>
                <div className="flex flex-col gap-1 transition-all duration-300 hover:-translate-y-1 group cursor-default">
                  <span className="text-xl font-extrabold text-white font-display group-hover:text-emerald-400 transition-colors duration-300">Ajax</span>
                  <span className="text-xs font-semibold text-slate-300 uppercase tracking-wider">Autoriseret Sikring</span>
                </div>
              </div>
            </div>

            {/* Hero Visuals (Asymmetric Collage) */}
            <div className="lg:col-span-5 relative">
              <div className="relative mx-auto max-w-[450px] lg:max-w-none">
                {/* Main Large Image */}
                <div 
                  className="relative overflow-hidden rounded-3xl border-4 border-white bg-white shadow-2xl rotate-[-2deg] transition-transform hover:rotate-0 duration-500"
                >
                  <img
                    src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/a2859b51-1b34-4d15-a97b-9fa5c0c0144f/webp-portservise-cover-2-85-11zon.webp"
                    alt="Pabst Teknik servicemontør udfører lovpligtigt eftersyn og service på industriport"
                    className="h-[350px] w-full object-cover md:h-[400px] transition-transform duration-500 hover:scale-105"
                  />
                </div>

                {/* Overlapping Secondary Image (Video Trigger) */}
                <div 
                  className="absolute -top-14 -right-12 lg:-top-16 lg:-right-14 w-2/3 overflow-hidden rounded-2xl border-4 border-white bg-white shadow-2xl rotate-[4deg] transition-transform hover:rotate-0 duration-500 hidden sm:block z-10 cursor-pointer group"
                  onClick={() => setIsVideoOpen(true)}
                >
                  <img
                    src="https://assets.auxodev.dk/cdn-cgi/image/width=400,quality=85,format=auto/assets/a2859b51-1b34-4d15-a97b-9fa5c0c0144f/webp-starterkit-cam-plus-89-11zon.webp"
                    alt="Ajax Systems StarterKit Cam Plus - Trådløst alarmsystem med fotoverifikation og central hub"
                    className="h-[180px] w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  {/* Play Button Overlay */}
                  <div className="absolute inset-0 flex items-center justify-center bg-slate-900/30 transition-opacity duration-300 group-hover:bg-slate-900/40">
                    <div className="flex h-14 w-14 items-center justify-center rounded-full bg-white/90 text-brand-accent shadow-2xl transition-all duration-300 group-hover:scale-110 group-hover:bg-white" aria-label="Afspil video om Pabst Teknik og Ajax sikring">
                      <i className="fa-solid fa-play text-xl ml-0.5" />
                    </div>
                  </div>
                  {/* Video Badge */}
                  <div className="absolute bottom-3 right-3 rounded-lg bg-brand-dark/80 px-2.5 py-1 text-xs font-bold text-white backdrop-blur-xs">
                    <i className="fa-regular fa-circle-play mr-1.5 text-brand-accent animate-pulse" />
                    Se video
                  </div>
                </div>

                {/* Decorative Elements */}
                <div className="absolute -right-6 -top-6 -z-10 h-32 w-32 rounded-full bg-brand-accent-light opacity-60 blur-2xl" />
                <div className="absolute -left-12 -bottom-12 -z-10 h-40 w-40 rounded-full bg-sky-100 opacity-60 blur-2xl" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="bg-white px-6 py-24 md:py-32">
        <div className="mx-auto max-w-7xl">
          <div className="text-center max-w-3xl mx-auto mb-16 flex flex-col gap-4">
            <span className="text-xs font-bold text-brand-accent uppercase tracking-widest">Hvad vi tilbyder</span>
            <h2 className="text-3xl font-extrabold text-brand-dark sm:text-4xl">
              Specialister i Portservice, Porte & Sikring
            </h2>
            <p className="text-slate-500 leading-relaxed">
              Vi udfører professionel portservice og lovpligtigt eftersyn samt monterer alle typer porte som selvstændige specialer – og leverer markedets nyeste, intelligente Ajax sikringssystemer.
            </p>
          </div>

          {/* Core Port Services Grid */}
          <div className="mb-6 flex items-center justify-between">
            <h3 className="font-display text-lg font-bold text-brand-dark flex items-center gap-2">
              <i className="fa-solid fa-garage text-brand-accent" />
              <span>Portservice & Porte</span>
            </h3>
            <Link
              to="/ydelser"
              className="inline-flex items-center gap-2 rounded-full bg-brand-accent/10 border border-brand-accent/20 px-3.5 py-1.5 text-xs sm:text-sm font-bold text-brand-accent transition-all hover:bg-brand-accent hover:text-white shadow-xs"
            >
              <span>Se hvad vi tilbyder</span>
              <i className="fa-solid fa-arrow-right text-xs" />
            </Link>
          </div>

          <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3 mb-16">
            {sortServices(services)
              .filter(s => !s.service_slug?.startsWith('alarmsystem'))
              .slice(0, 6)
              .map((service) => {
                const imageUrl = service.image && service.image[0]
                  ? service.image[0].replace('https://assets.auxodev.dk/assets/', 'https://assets.auxodev.dk/cdn-cgi/image/width=400,height=250,fit=cover,quality=80,format=auto/assets/')
                  : null;

                return (
                  <div
                    key={service.service_slug}
                    className="group relative flex flex-col overflow-hidden rounded-2xl border border-slate-100 bg-slate-50 transition-all hover:border-slate-200 hover:bg-white hover:shadow-xl"
                  >
                    {/* Image Header */}
                    <div className="relative h-48 w-full overflow-hidden bg-slate-200">
                      {imageUrl ? (
                        <img
                          src={imageUrl}
                          alt={`${service.title.da} - Professionel portservice og montageløsning fra Pabst Teknik ApS`}
                          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center bg-slate-100 text-slate-400">
                          <i className="fa-regular fa-image text-3xl" />
                        </div>
                      )}
                      {/* Icon Overlay */}
                      <div className="absolute bottom-4 left-4 flex h-12 w-12 items-center justify-center rounded-xl bg-white/90 backdrop-blur-sm text-brand-accent shadow-md transition-all group-hover:bg-brand-accent group-hover:text-white">
                        <i className={`${getIconClass(service.icon)} text-xl`} />
                      </div>
                    </div>

                    {/* Content */}
                    <div className="flex flex-col flex-grow p-6">
                      <h3 className="font-display text-lg font-bold text-brand-dark mb-3 group-hover:text-brand-accent transition-colors">
                        {service.title.da}
                      </h3>
                      <p className="text-sm text-slate-500 leading-relaxed mb-6 flex-grow">
                        {service.summary.da}
                      </p>
                      <Link
                        to={`/ydelser/${service.service_slug}`}
                        className="inline-flex items-center gap-2 text-sm font-bold text-brand-accent group-hover:text-brand-accent-hover mt-auto"
                      >
                        <span>Læs mere</span>
                        <i className="fa-regular fa-arrow-right text-xs transition-transform group-hover:translate-x-1" />
                      </Link>
                    </div>
                  </div>
                );
              })}
          </div>

          {/* Highlight Section for Ajax Sikring (Nyhed) */}
          <div className="rounded-3xl bg-gradient-to-br from-slate-950 via-slate-900 to-brand-dark p-8 md:p-12 text-white shadow-2xl relative overflow-hidden">
            <div className="absolute right-0 top-0 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl pointer-events-none" />
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center relative z-10">
              <div className="lg:col-span-7 flex flex-col gap-5">
                <div className="inline-flex items-center gap-2 rounded-full bg-emerald-500/20 border border-emerald-500/30 px-3.5 py-1 text-xs font-bold text-emerald-400 uppercase tracking-wider self-start">
                  <i className="fa-solid fa-shield-check text-xs" />
                  Nyhed hos Pabst Teknik: Sikring & Alarmer
                </div>
                <h3 className="text-2xl sm:text-3xl font-extrabold text-white font-display">
                  Intelligent Sikring med <span className="text-emerald-400">Ajax Systems</span>
                </h3>
                <p className="text-slate-300 text-sm sm:text-base leading-relaxed">
                  Som en stærk nyere gren af vores forretning leverer og monterer vi markedets mest avancerede trådløse sikringsløsninger. Få fuld tryghed og kontrol direkte på din smartphone med videoovervågning, indbrudsalarmer, brand- og røgdetektion samt automatisk vandstop.
                </p>
                <div className="flex flex-wrap gap-3 pt-2">
                  <Link
                    to="/ydelser/alarmsystem"
                    className="inline-flex items-center gap-2 rounded-full bg-emerald-500 px-6 py-3 text-xs sm:text-sm font-bold text-slate-950 shadow-lg hover:bg-emerald-400 transition-colors"
                  >
                    <span>Udforsk Ajax Alarmsystemer</span>
                    <i className="fa-regular fa-arrow-right" />
                  </Link>
                  <Link
                    to="/ydelser/alarmsystem-video-overvaagning"
                    className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/20 px-6 py-3 text-xs sm:text-sm font-bold text-white hover:bg-white/20 transition-colors"
                  >
                    <span>Videoovervågning</span>
                  </Link>
                </div>
              </div>

              <div className="lg:col-span-5 flex justify-center">
                <div className="relative overflow-hidden rounded-2xl border-2 border-white/20 shadow-xl max-w-sm">
                  <img
                    src="https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=85,format=auto/assets/a2859b51-1b34-4d15-a97b-9fa5c0c0144f/webp-starterkit-cam-plus-89-11zon.webp"
                    alt="Ajax StarterKit Cam Plus med trådløs bevægelsesdetektor og hub til tyverisikring"
                    className="w-full h-56 sm:h-64 object-cover"
                  />
                  <div className="absolute bottom-3 left-3 right-3 rounded-lg bg-slate-950/80 backdrop-blur-xs p-2.5 text-center text-xs font-semibold text-slate-200">
                    Autoriseret Ajax installatør i Vejen & hele Sydøstjylland
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center mt-12">
            <Link
              to="/ydelser"
              className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-8 py-3.5 text-sm font-bold text-brand-dark shadow-sm transition-all hover:border-brand-accent hover:text-brand-accent"
            >
              <span>Se alt hvad vi tilbyder</span>
              <i className="fa-regular fa-arrow-right" />
            </Link>
          </div>
        </div>
      </section>

      {/* SEO Information & Advice Section */}
      <section className="bg-slate-50 border-t border-slate-200/70 px-6 py-20 md:py-28 relative overflow-hidden">
        {/* Subtle background decoration */}
        <div className="absolute top-0 right-1/4 w-96 h-96 bg-brand-accent/5 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-10 w-80 h-80 bg-slate-200/50 rounded-full blur-2xl pointer-events-none" />

        <div className="mx-auto max-w-7xl relative z-10">
          {/* Section Header */}
          <div className="text-center max-w-3xl mx-auto mb-16 flex flex-col gap-4">
            <div className="inline-flex items-center gap-2 rounded-full bg-brand-accent/10 border border-brand-accent/20 px-3.5 py-1.5 text-xs font-bold text-brand-accent uppercase tracking-wider self-center">
              <i className="fa-regular fa-shield-check text-xs" />
              Viden & Rådgivning
            </div>
            <h2 className="text-3xl font-extrabold text-brand-dark sm:text-4xl lg:text-5xl font-display tracking-tight">
              Faglig rådgivning om porte og service
            </h2>
            <p className="text-slate-600 text-base sm:text-lg leading-relaxed mt-2">
              Få et hurtigt overblik over vores services, vedligeholdelse, garageporte og industriporte i Vejen og resten af Trekantsområdet.
            </p>
          </div>

          {/* Divided into 3 Distinct, Clear Thematic Sections */}
          <div className="space-y-12 sm:space-y-16">
            
            {/* Section 1: Portservice, reparation & eftersyn (3 Balanced Cards) */}
            <div className="space-y-6">
              <div className="flex items-center gap-3 border-b border-slate-200 pb-3">
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-brand-accent/15 text-xs font-bold text-brand-accent">
                  1
                </span>
                <h3 className="text-xl sm:text-2xl font-bold text-brand-dark font-display">
                  Portservice, reparation & faste aftaler
                </h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Card 1: Portens levetid */}
                <div className="flex flex-col bg-white rounded-2xl border border-slate-200/80 p-6 sm:p-7 shadow-xs hover:shadow-md transition-all duration-300">
                  <div className="flex items-center gap-3.5 mb-4">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-brand-accent/10 text-brand-accent">
                      <i className="fa-regular fa-arrows-rotate text-lg" />
                    </div>
                    <h4 className="font-display text-base sm:text-lg font-bold text-brand-dark leading-snug">
                      Få hjælp gennem hele portens levetid
                    </h4>
                  </div>
                  <p className="text-slate-600 text-sm sm:text-base leading-relaxed">
                    En port skal fungere sikkert hver dag, uanset om den beskytter familiens garage eller er vigtig for vareflowet i en virksomhed. Derfor ser vi på hele løsningen. Vi rådgiver om valg og montage af nye porte og hjælper efterfølgende med service, eftersyn og reparation, når behovet opstår.
                  </p>
                </div>

                {/* Card 2: Hvad gør vi, når porten ikke fungerer */}
                <div className="flex flex-col bg-white rounded-2xl border border-slate-200/80 p-6 sm:p-7 shadow-xs hover:shadow-md transition-all duration-300">
                  <div className="flex items-center gap-3.5 mb-4">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-brand-accent/10 text-brand-accent">
                      <i className="fa-regular fa-screwdriver-wrench text-lg" />
                    </div>
                    <h4 className="font-display text-base sm:text-lg font-bold text-brand-dark leading-snug">
                      Hvad gør vi, når porten ikke fungerer
                    </h4>
                  </div>
                  <p className="text-slate-600 text-sm sm:text-base leading-relaxed">
                    En port kan begynde at larme, køre ujævnt eller stoppe helt som følge af slid, fejl i automatikken eller en skade. Ved service af port gennemgår vi problemet og vurderer, hvad der skal repareres eller justeres. Vi arbejder på forskellige fabrikater og tager udgangspunkt i portens faktiske stand.
                  </p>
                </div>

                {/* Card 3: Fast serviceaftale */}
                <div className="flex flex-col bg-white rounded-2xl border border-slate-200/80 p-6 sm:p-7 shadow-xs hover:shadow-md transition-all duration-300">
                  <div className="flex items-center gap-3.5 mb-4">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-brand-accent/10 text-brand-accent">
                      <i className="fa-regular fa-calendar-check text-lg" />
                    </div>
                    <h4 className="font-display text-base sm:text-lg font-bold text-brand-dark leading-snug">
                      Få mere overblik med en fast serviceaftale
                    </h4>
                  </div>
                  <p className="text-slate-600 text-sm sm:text-base leading-relaxed">
                    Med en fast serviceaftale bliver den løbende vedligeholdelse lettere at planlægge. Vi kan holde styr på tidspunktet for det næste eftersyn og rådgive om lovpligtigt porteftersyn på erhvervsejendomme. Formålet er at opdage slid i tide og mindske risikoen for unødige driftsstop og større reparationer.
                  </p>
                </div>
              </div>
            </div>

            {/* Section 2: Portløsninger til bolig & erhverv (2 Wide Structured Cards) */}
            <div className="space-y-6">
              <div className="flex items-center gap-3 border-b border-slate-200 pb-3">
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-brand-accent/15 text-xs font-bold text-brand-accent">
                  2
                </span>
                <h3 className="text-xl sm:text-2xl font-bold text-brand-dark font-display">
                  Porte tilpasset private og erhverv
                </h3>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Card 4: Garageporte */}
                <div className="bg-white rounded-2xl border border-slate-200/80 p-7 sm:p-8 shadow-xs hover:shadow-md transition-all duration-300 flex flex-col justify-between">
                  <div>
                    <div className="flex items-center gap-4 mb-6">
                      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-amber-500/10 text-amber-600">
                        <i className="fa-regular fa-warehouse text-xl" />
                      </div>
                      <div>
                        <span className="text-[11px] font-bold tracking-wider text-amber-600 uppercase">Private Boliger</span>
                        <h4 className="font-display text-lg sm:text-xl font-bold text-brand-dark">
                          Garageporte der gør hverdagen nemmere
                        </h4>
                      </div>
                    </div>

                    <div className="space-y-4 text-slate-600 text-sm sm:text-base leading-relaxed">
                      <div className="bg-slate-50/80 p-4 rounded-xl border border-slate-100">
                        <p>
                          Til private leverer og monterer vi garageporte, der vælges efter garagens mål, boligens udtryk og den måde, porten skal bruges på. Du kan få hjælp til både en ny port til garage og service af den eksisterende løsning. Vi rådgiver blandt andet om isolering, betjening, sikkerhed og farvevalg.
                        </p>
                      </div>
                      <div className="bg-slate-50/80 p-4 rounded-xl border border-slate-100">
                        <p>
                          Elektriske garageporte gør den daglige adgang nem, fordi porten kan åbnes og lukkes med automatik. Har du brug for almindelig adgang uden at åbne hele porten, kan en port med dør være relevant. Vi hjælper dig med at afklare mulighederne, så funktion og konstruktion passer til dit behov.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 pt-4 border-t border-slate-100 flex flex-wrap items-center justify-between gap-3">
                    <span className="text-xs text-slate-500 font-medium">Automatik, dør i port & isolering</span>
                    <Link
                      to="/ydelser/garageporte"
                      className="text-xs font-bold text-brand-accent hover:text-brand-accent-hover inline-flex items-center gap-1.5"
                    >
                      <span>Læs mere om garageporte</span>
                      <i className="fa-regular fa-arrow-right text-[10px]" />
                    </Link>
                  </div>
                </div>

                {/* Card 5: Industriporte */}
                <div className="bg-white rounded-2xl border border-slate-200/80 p-7 sm:p-8 shadow-xs hover:shadow-md transition-all duration-300 flex flex-col justify-between">
                  <div>
                    <div className="flex items-center gap-4 mb-6">
                      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-blue-500/10 text-blue-600">
                        <i className="fa-regular fa-industry text-xl" />
                      </div>
                      <div>
                        <span className="text-[11px] font-bold tracking-wider text-blue-600 uppercase">Erhverv & Industri</span>
                        <h4 className="font-display text-lg sm:text-xl font-bold text-brand-dark">
                          Industriporte skal vælges efter driften
                        </h4>
                      </div>
                    </div>

                    <div className="space-y-4 text-slate-600 text-sm sm:text-base leading-relaxed">
                      <div className="bg-slate-50/80 p-4 rounded-xl border border-slate-100">
                        <p>
                          Industriporte skal passe til trafikken, bygningen og de praktiske krav i driften. I en opvarmet hal kan isolering være vigtig, mens lysindfald, automatik og en robust overflade kan være afgørende andre steder. Vi hjælper med montage, portservice og reparation for virksomheder og industri.
                        </p>
                      </div>
                      <div className="bg-slate-50/80 p-4 rounded-xl border border-slate-100">
                        <p>
                          Vores løsninger omfatter også hurtigporte, frostporte og frostdøre samt låger, lågeautomatik, læsseramper og sluser til læsseområder. Det giver mulighed for at samle flere adgangsløsninger hos én faglig samarbejdspartner, som kan se på både funktion, sikkerhed og løbende vedligeholdelse.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 pt-4 border-t border-slate-100 flex flex-wrap items-center justify-between gap-3">
                    <span className="text-xs text-slate-500 font-medium">Hurtigporte, frostdøre, læsseramper & sluser</span>
                    <Link
                      to="/ydelser/industriporte"
                      className="text-xs font-bold text-brand-accent hover:text-brand-accent-hover inline-flex items-center gap-1.5"
                    >
                      <span>Læs om industriporte</span>
                      <i className="fa-regular fa-arrow-right text-[10px]" />
                    </Link>
                  </div>
                </div>
              </div>
            </div>

            {/* Section 3: Lokal Portservice i Vejen & Trekantsområdet (Full Width Featured Card) */}
            <div className="space-y-6">
              <div className="flex items-center gap-3 border-b border-slate-200 pb-3">
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-brand-accent/15 text-xs font-bold text-brand-accent">
                  3
                </span>
                <h3 className="text-xl sm:text-2xl font-bold text-brand-dark font-display">
                  Lokal forankring & uforpligtende tilbud
                </h3>
              </div>

              <div className="bg-gradient-to-br from-slate-900 to-brand-dark text-white rounded-3xl border border-slate-800 p-8 sm:p-10 lg:p-12 shadow-xl relative overflow-hidden">
                <div className="absolute top-0 right-0 w-96 h-96 bg-brand-accent/10 rounded-full blur-3xl pointer-events-none" />

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center relative z-10">
                  <div className="lg:col-span-8 space-y-5">
                    <div className="flex items-center gap-3">
                      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-brand-accent text-white shadow-sm">
                        <i className="fa-regular fa-map-location-dot text-lg" />
                      </div>
                      <h4 className="font-display text-xl sm:text-2xl font-bold text-white">
                        Hvorfor vælge lokal portservice i Vejen
                      </h4>
                    </div>

                    <div className="space-y-4 text-slate-300 text-sm sm:text-base leading-relaxed">
                      <p>
                        Søger du portservice i Vejen, kommer vi med lokal forankring og bred erfaring med porte til både bolig og erhverv. Vi kører også til kunder i Kolding, Christiansfeld, Fredericia, Vejle og Billund samt resten af Trekantsområdet. Her hjælper vi med alt fra garageporte til større portløsninger i erhvervsbygninger.
                      </p>
                      <p>
                        Kontakt Pabst Teknik, hvis du vil have vurderet en eksisterende port eller ønsker rådgivning om en ny løsning. Fortæl os om bygningen, portens brug og de udfordringer, du oplever. Så kan vi tage en faglig dialog om montage, reparation eller portservice og udarbejde et uforpligtende tilbud.
                      </p>
                    </div>

                    {/* Area Badges */}
                    <div className="flex flex-wrap items-center gap-2 pt-2">
                      <span className="text-xs font-semibold text-slate-400 mr-1">Dækningsområde:</span>
                      {['Vejen', 'Kolding', 'Christiansfeld', 'Fredericia', 'Vejle', 'Billund', 'Hele Trekantområdet'].map((by) => (
                        <span key={by} className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/10 text-xs font-medium text-slate-200 border border-white/10">
                          <i className="fa-solid fa-location-dot text-brand-accent text-[10px]" />
                          {by}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="lg:col-span-4 flex flex-col gap-4 bg-white/5 border border-white/10 rounded-2xl p-6 backdrop-blur-xs">
                    <h5 className="font-display text-base font-bold text-white flex items-center gap-2">
                      <i className="fa-regular fa-comment-dots text-brand-accent" />
                      Start en dialog med os
                    </h5>
                    <p className="text-xs text-slate-300 leading-relaxed">
                      Vi svarer hurtigt og rådgiver gerne om den rette løsning til din bygning og dine behov.
                    </p>
                    <div className="flex flex-col gap-3 pt-2">
                      <Link
                        to="/kontakt"
                        className="inline-flex items-center justify-center gap-2 rounded-full bg-brand-accent px-6 py-3 text-sm font-bold text-white shadow-md hover:bg-brand-accent-hover transition-colors"
                      >
                        <span>Få et uforpligtende tilbud</span>
                        <i className="fa-regular fa-arrow-right text-xs" />
                      </Link>
                      <a
                        href="tel:+4521684422"
                        className="inline-flex items-center justify-center gap-2 rounded-full bg-white/10 border border-white/20 px-5 py-2.5 text-sm font-semibold text-white hover:bg-white/20 transition-colors"
                      >
                        <i className="fa-regular fa-phone text-xs text-brand-accent" />
                        <span>Ring 21 68 44 22</span>
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Trust & Quality Section */}
      <section className="px-6 py-24 md:py-32">
        <div className="mx-auto max-w-7xl">
          <div className="grid grid-cols-1 items-center gap-16 lg:grid-cols-2">
            {/* Visuals */}
            <div className="relative order-2 lg:order-1">
              <div className="relative mx-auto max-w-[450px] lg:max-w-none">
                <div className="overflow-hidden rounded-3xl border-4 border-white bg-white shadow-2xl rotate-[2deg]">
                  <img
                    src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/a2859b51-1b34-4d15-a97b-9fa5c0c0144f/webp-img20240717114443-69-11zon.webp"
                    alt="Pabst Teknik tekniker udfører professionelt service og montage på industriport"
                    className="h-[350px] w-full object-cover md:h-[400px]"
                  />
                </div>
                <div className="absolute -right-6 -bottom-6 w-1/2 overflow-hidden rounded-2xl border-4 border-white bg-white shadow-2xl rotate-[-4deg] hidden sm:block">
                  <img
                    src="https://assets.auxodev.dk/cdn-cgi/image/width=400,quality=85,format=auto/assets/a2859b51-1b34-4d15-a97b-9fa5c0c0144f/webp-img-20220117-083136-01-scaled-68-11zon.webp"
                    alt="Årligt lovpligtigt eftersyn og teknisk vedligeholdelse af ledhejseporte"
                    className="h-[180px] w-full object-cover"
                  />
                </div>
              </div>
            </div>

            {/* Text Content */}
            <div className="flex flex-col gap-8 order-1 lg:order-2">
              <span className="text-xs font-bold text-brand-accent uppercase tracking-widest">Hvorfor vælge os?</span>
              <h2 className="text-3xl font-extrabold text-brand-dark sm:text-4xl">
                Din tryghed er vores højeste prioritet
              </h2>
              <p className="text-slate-600 leading-relaxed">
                Hos Pabst Teknik ApS går vi aldrig på kompromis med kvaliteten. Vi forstår, at sikkerhed og velfungerende teknik er fundamentet for en tryg hverdag og en stabil erhvervsdrift.
              </p>

              <div className="flex flex-col gap-6">
                <div className="flex gap-4">
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-brand-accent-light text-brand-accent">
                    <i className="fa-regular fa-user-helmet-safety text-xl" />
                  </span>
                  <div>
                    <h4 className="font-display text-base font-bold text-brand-dark mb-1">Faglig ekspertise</h4>
                    <p className="text-sm text-slate-500 leading-relaxed">Vi er uddannede og certificerede teknikere med mange års erfaring inden for sikring og portservice.</p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-brand-accent-light text-brand-accent">
                    <i className="fa-regular fa-clock text-xl" />
                  </span>
                  <div>
                    <h4 className="font-display text-base font-bold text-brand-dark mb-1">Hurtig udrykning</h4>
                    <p className="text-sm text-slate-500 leading-relaxed">Vi er lokalt forankret i Vejen, hvilket betyder kort responstid, når du har akut brug for hjælp.</p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-brand-accent-light text-brand-accent">
                    <i className="fa-regular fa-award text-xl" />
                  </span>
                  <div>
                    <h4 className="font-display text-base font-bold text-brand-dark mb-1">Godkendte kvalitetsprodukter</h4>
                    <p className="text-sm text-slate-500 leading-relaxed">Vi samarbejder kun med anerkendte producenter for at sikre dig langtidsholdbare og stabile løsninger.</p>
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-4 pt-2">
                <Link
                  to="/om-os"
                  className="inline-flex items-center gap-2 rounded-full bg-brand-dark px-6 py-3 text-sm font-bold text-white shadow-md hover:bg-slate-800 transition-all"
                >
                  <span>Læs mere om vores baggrund</span>
                  <i className="fa-regular fa-arrow-right text-xs text-brand-accent" />
                </Link>
                <Link
                  to="/galleri"
                  className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-6 py-3 text-sm font-bold text-brand-dark hover:bg-slate-200 transition-all"
                >
                  <i className="fa-regular fa-images text-xs text-brand-accent" />
                  <span>Se vores arbejde i galleriet</span>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Local Anchoring Section */}
      <section className="bg-brand-dark text-white px-6 py-24 md:py-32 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img
            src="https://assets.auxodev.dk/assets/a2859b51-1b34-4d15-a97b-9fa5c0c0144f/webp-house-scaled-66-11zon.webp"
            alt="Bolig og erhvervsbygninger i Vejen og omegn med professionelle porte og sikring"
            className="h-full w-full object-cover filter grayscale"
          />
        </div>
        <div className="mx-auto max-w-7xl relative z-10">
          <div className="grid grid-cols-1 gap-16 lg:grid-cols-12 items-center">
            <div className="lg:col-span-7 flex flex-col gap-6">
              <span className="text-xs font-bold text-brand-accent uppercase tracking-widest">Lokal forankring & Landsdækkende rækkevidde</span>
              <h2 className="text-3xl font-extrabold text-white sm:text-4xl">
                Vi dækker Vejen og hele Trekantsområdet
              </h2>
              <p className="text-slate-300 leading-relaxed text-lg">
                Med et centralt beliggende firma er vi altid tæt på vores kunder. Uanset om du har brug for et årligt eftersyn af industriporten i virksomheden, eller du ønsker at sikre dit private hjem mod indbrud, rykker vi hurtigt ud – vi dækker fra øst til vest.
              </p>
              <div className="flex flex-wrap gap-4 mt-2">
                <div className="rounded-xl bg-white/5 border border-white/10 px-5 py-3 text-sm font-semibold">
                  <i className="fa-regular fa-location-dot text-brand-accent mr-2" /> Vejen & Gesten
                </div>
                <div className="rounded-xl bg-white/5 border border-white/10 px-5 py-3 text-sm font-semibold">
                  <i className="fa-regular fa-location-dot text-brand-accent mr-2" /> Kolding, Christiansfeld, Vejle & Fredericia
                </div>
                <div className="rounded-xl bg-white/5 border border-white/10 px-5 py-3 text-sm font-semibold">
                  <i className="fa-regular fa-location-dot text-brand-accent mr-2" /> Hele Trekantsområdet
                </div>
              </div>
            </div>
            <div className="lg:col-span-5 bg-white/5 border border-white/10 rounded-2xl p-8 backdrop-blur-sm">
              <h3 className="font-display text-xl font-bold text-white mb-4">Akut hjælp eller rådgivning?</h3>
              <p className="text-sm text-slate-300 leading-relaxed mb-6">
                Har du en port, der ikke vil lukke, eller en alarm, der driller? Ring direkte til os – vi sidder klar til at hjælpe dig.
              </p>
              <div className="flex flex-col gap-3">
                <a
                  href="tel:+4521684422"
                  className="flex items-center justify-center gap-3 rounded-xl bg-brand-accent py-4 text-base font-bold text-white shadow-lg transition-all hover:bg-brand-accent-hover"
                >
                  <i className="fa-regular fa-phone text-lg" />
                  <span>Ring nu: 21 68 44 22</span>
                </a>
                <Link
                  to="/om-os"
                  className="flex items-center justify-center gap-2 rounded-xl bg-white/10 border border-white/15 py-3 text-sm font-semibold text-white hover:bg-white/20 transition-all text-center"
                >
                  <span>Læs mere om Pabst Teknik</span>
                  <i className="fa-regular fa-arrow-right text-xs" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Gallery Preview */}
      {galleryImages.length > 0 && (
        <section className="bg-white px-6 py-24 md:py-32 overflow-hidden">
          <div className="mx-auto max-w-7xl">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
              <div className="flex flex-col gap-4 max-w-2xl">
                <span className="text-xs font-bold text-brand-accent uppercase tracking-widest">Billeder fra hverdagen</span>
                <h2 className="text-3xl font-extrabold text-brand-dark sm:text-4xl">
                  Vores seneste projekter
                </h2>
                <p className="text-slate-500 leading-relaxed">
                  Se et udvalg af vores udførte opgaver inden for alarmsystemer, videoovervågning og portservice. Klik på et billede for at se det i fuld størrelse.
                </p>
              </div>
              
              <div className="flex items-center gap-3 shrink-0 self-start md:self-auto">
                {/* Navigation Arrows */}
                <button
                  onClick={prevSlide}
                  disabled={currentIndex === 0}
                  className="flex h-12 w-12 items-center justify-center rounded-full border border-slate-200 bg-white text-brand-dark shadow-sm transition-all hover:border-brand-accent hover:text-brand-accent disabled:opacity-30 disabled:hover:border-slate-200 disabled:hover:text-brand-dark cursor-pointer disabled:cursor-not-allowed"
                  aria-label="Forrige billede"
                >
                  <i className="fa-regular fa-chevron-left text-base" />
                </button>
                <button
                  onClick={nextSlide}
                  disabled={currentIndex >= galleryImages.length - visibleSlides}
                  className="flex h-12 w-12 items-center justify-center rounded-full border border-slate-200 bg-white text-brand-dark shadow-sm transition-all hover:border-brand-accent hover:text-brand-accent disabled:opacity-30 disabled:hover:border-slate-200 disabled:hover:text-brand-dark cursor-pointer disabled:cursor-not-allowed"
                  aria-label="Næste billede"
                >
                  <i className="fa-regular fa-chevron-right text-base" />
                </button>
                <Link
                  to="/galleri"
                  className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-6 py-3 text-sm font-bold text-brand-dark shadow-sm transition-all hover:border-brand-accent hover:text-brand-accent"
                >
                  <span>Gå til galleri</span>
                  <i className="fa-regular fa-arrow-right" />
                </Link>
              </div>
            </div>

            {/* Slider Container */}
            <div className="relative -mx-4 px-4">
              <div className="overflow-hidden rounded-3xl">
                <div
                  className="flex transition-transform duration-500 ease-out"
                  style={{
                    transform: `translateX(-${currentIndex * (100 / visibleSlides)}%)`,
                  }}
                >
                  {galleryImages.map((img, idx) => (
                    <div
                      key={idx}
                      className="shrink-0 px-2.5"
                      style={{ width: `${100 / visibleSlides}%` }}
                    >
                      <div
                        onClick={() => setLightboxIndex(idx)}
                        className="group relative h-[450px] sm:h-[550px] md:h-[600px] overflow-hidden rounded-2xl bg-slate-100 cursor-pointer shadow-sm transition-all hover:shadow-md"
                      >
                        <img
                          src={img.includes('assets.auxodev.dk') ? img.replace('assets.auxodev.dk/assets/', 'assets.auxodev.dk/cdn-cgi/image/width=1000,quality=85,format=auto/assets/') : img}
                          alt={`Referenceprojekt ${idx + 1} - Udført portservice og montage fra Pabst Teknik ApS i Vejen`}
                          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                          loading="lazy"
                        />
                        <div className="absolute inset-0 bg-slate-950/40 opacity-0 transition-opacity duration-300 group-hover:opacity-100 flex items-center justify-center">
                          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-white text-brand-dark shadow-lg transform translate-y-4 transition-transform duration-300 group-hover:translate-y-0">
                            <i className="fa-regular fa-magnifying-glass-plus text-xl" />
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Pagination Dots */}
            {galleryImages.length > visibleSlides && (
              <div className="flex justify-center gap-2 mt-8">
                {Array.from({ length: galleryImages.length - visibleSlides + 1 }).map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentIndex(idx)}
                    className={`h-2.5 rounded-full transition-all duration-300 cursor-pointer ${
                      currentIndex === idx ? 'w-8 bg-brand-accent' : 'w-2.5 bg-slate-200 hover:bg-slate-300'
                    }`}
                    aria-label={`Gå til slide ${idx + 1}`}
                  />
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Testimonials Section */}
      <section className="px-6 py-20 md:py-28 bg-white border-t border-slate-100">
        <div className="mx-auto max-w-7xl">
          <div className="text-center max-w-3xl mx-auto mb-16 flex flex-col gap-4">
            <span className="text-xs font-bold text-brand-accent uppercase tracking-widest">Kundeanmeldelser</span>
            <h2 className="text-3xl font-extrabold text-brand-dark sm:text-4xl font-display">
              Hvad vores kunder siger
            </h2>
            <p className="text-slate-500 leading-relaxed">
              Vi sætter en stor ære i at levere service i topklasse. Her er et udvalg af udtalelser fra vores glade kunder.
            </p>
          </div>

          <div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
          >
            {TESTIMONIALS.map((item, idx) => (
              <div
                key={idx}
                className="rounded-2xl bg-slate-50 border border-slate-100 p-6 md:p-7 shadow-sm hover:shadow-md transition-all duration-300 flex flex-col justify-between group hover:-translate-y-1"
              >
                <div className="flex flex-col gap-4">
                  <div className="flex items-center justify-between">
                    <div className="flex gap-1 text-amber-400 text-sm">
                      {[...Array(5)].map((_, i) => (
                        <i key={i} className="fa-solid fa-star" />
                      ))}
                    </div>
                    <span className="text-slate-300 group-hover:text-brand-accent/40 transition-colors">
                      <i className="fa-solid fa-quote-right text-lg" />
                    </span>
                  </div>
                  <p className="text-sm text-slate-700 leading-relaxed italic font-normal">
                    "{item.quote}"
                  </p>
                </div>
                <div className="pt-6 mt-4 border-t border-slate-200/60">
                  <h4 className="font-display text-sm font-bold text-brand-dark">
                    {item.author}
                  </h4>
                  <p className="text-[11px] text-slate-400 font-semibold uppercase tracking-wider mt-0.5">
                    {item.role}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="px-6 py-24 md:py-32 bg-slate-50">
        <div className="mx-auto max-w-7xl">
          <div className="grid grid-cols-1 gap-16 lg:grid-cols-12 items-start">
            {/* Contact Info */}
            <div className="lg:col-span-5 flex flex-col gap-8">
              <div className="flex flex-col gap-4">
                <span className="text-xs font-bold text-brand-accent uppercase tracking-widest">Kontakt os</span>
                <h2 className="text-3xl font-extrabold text-brand-dark sm:text-4xl">
                  Lad os tage en snak om din opgave
                </h2>
                <p className="text-slate-600 leading-relaxed">
                  Uanset om du har brug for akut portservice, rådgivning om indbrudssikring eller et komplet tilbud på videoovervågning, er du altid velkommen til at kontakte os.
                </p>
              </div>

              <div className="flex flex-col gap-6">
                <div className="flex gap-4 items-start">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white border border-slate-200 text-brand-accent shadow-sm">
                    <i className="fa-regular fa-phone text-lg" />
                  </span>
                  <div>
                    <h4 className="font-display text-sm font-bold text-brand-dark mb-0.5">Telefon</h4>
                    <a href="tel:+4521684422" className="text-base font-semibold text-slate-600 hover:text-brand-accent transition-colors whitespace-nowrap">21 68 44 22</a>
                  </div>
                </div>

                <div className="flex gap-4 items-start">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white border border-slate-200 text-brand-accent shadow-sm">
                    <i className="fa-regular fa-envelope text-lg" />
                  </span>
                  <div>
                    <h4 className="font-display text-sm font-bold text-brand-dark mb-0.5">E-mail</h4>
                    <a href="mailto:kontakt@pabstteknik.dk" className="text-base font-semibold text-slate-600 hover:text-brand-accent transition-colors whitespace-nowrap">kontakt@pabstteknik.dk</a>
                  </div>
                </div>

                <div className="flex gap-4 items-start">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white border border-slate-200 text-brand-accent shadow-sm">
                    <i className="fa-regular fa-map-pin text-lg" />
                  </span>
                  <div>
                    <h4 className="font-display text-sm font-bold text-brand-dark mb-0.5">Adresse</h4>
                    <p className="text-base font-semibold text-slate-600 leading-relaxed">Gestenvej 29, 6600 Vejen</p>
                  </div>
                </div>

                <div className="flex gap-4 items-start">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white border border-slate-200 text-brand-accent shadow-sm">
                    <i className="fa-regular fa-id-card text-lg" />
                  </span>
                  <div>
                    <h4 className="font-display text-sm font-bold text-brand-dark mb-0.5">CVR-nummer</h4>
                    <p className="text-base font-semibold text-slate-600">42228842</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-7">
              <ContactForm />
            </div>
          </div>
        </div>
      </section>

      {/* Lightbox */}
      {lightboxIndex !== null && (
        <Lightbox
          images={galleryImages}
          currentIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onPrev={() => setLightboxIndex((lightboxIndex - 1 + galleryImages.length) % galleryImages.length)}
          onNext={() => setLightboxIndex((lightboxIndex + 1) % galleryImages.length)}
        />
      )}

      {/* Video Modal */}
      {isVideoOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 p-4 backdrop-blur-sm">
          <div className="relative w-full max-w-4xl overflow-hidden rounded-2xl bg-black shadow-2xl">
            {/* Close Button */}
            <button
              onClick={() => setIsVideoOpen(false)}
              className="absolute top-4 right-4 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-slate-900/80 text-white transition-all hover:bg-slate-800 hover:scale-105 cursor-pointer"
              aria-label="Luk video"
            >
              <i className="fa-solid fa-xmark text-lg" />
            </button>
            {/* Aspect Ratio Container for YouTube Embed */}
            <div className="aspect-video w-full">
              <iframe
                src="https://www.youtube.com/embed/0T5Sk6xCKek?autoplay=1&rel=0"
                title="Pabst Teknik præsentationsvideo - Intelligent Ajax alarmsystem og portløsninger"
                className="h-full w-full border-0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
